import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Flame, Heart, Moon, Shield, Sparkles, Star, Sword } from 'lucide-react';
import { CARD_COLOR_HEX, MAX_CARD_LEVEL, cardCustomizationAtLevel, scaleCombatStats, type CardCustomization } from '@anidex/shared/economy';
import type { CharacterDTO } from '@anidex/shared/types';
import { useCardPresence } from '../hooks/useCardPresence';
import { useTilt } from '../hooks/useTilt';
import { useOwnedCharacters } from '../services/queries';
import { rarityLabel } from '../services/format';
import { isPrestige, PrestigeAura, PrestigeFx } from './card-fx/PrestigeFx';
import { FragmentCrystal } from './FragmentCrystal';
import { CheckIcon, CloseIcon, HeartIcon, LockIcon, StarIcon } from './Icons';

export interface CardOwnership {
  fragments: number;
  upgradeLevel: number;
  isFavorite?: boolean;
  nametag?: string | null;
  customization?: CardCustomization;
  customImageUrl?: string | null;
  lock?: { kind: string; id: string } | null;
}

const EMBLEM_ICONS = { star: Star, heart: Heart, moon: Moon, flame: Flame, crown: Crown, sword: Sword };

export function CardEmblem({ emblem, size = 18 }: { emblem: NonNullable<CardCustomization['emblem']>; size?: number }) {
  const Icon = EMBLEM_ICONS[emblem];
  return <Icon size={size} strokeWidth={1.8} aria-hidden="true" />;
}

interface CharacterCardProps {
  character: CharacterDTO;
  ownership?: CardOwnership | null;
  nametag?: string | null;
  missing?: boolean;
  size?: 'sm' | 'md' | 'lg';
  tilt?: boolean;
  tag?: ReactNode;
  sparkleCrystal?: boolean;
  eager?: boolean;
  pressed?: boolean;
  onSelect?: () => void;
  onFavorite?: () => void;
  wished?: boolean;
  onWish?: () => void;
  // Extra control layered above the hit area (e.g. image upload for image editors).
  action?: ReactNode;
}

export function CharacterCard({
  character,
  ownership = null,
  nametag,
  missing = false,
  size = 'md',
  tilt = true,
  tag,
  sparkleCrystal = false,
  eager = false,
  pressed = false,
  onSelect,
  onFavorite,
  wished = false,
  onWish,
  action,
}: CharacterCardProps) {
  const { t } = useTranslation('collection');
  const level = Math.min(MAX_CARD_LEVEL, Math.max(1, Math.floor(ownership?.upgradeLevel ?? 1)));
  const customization = missing ? {} : cardCustomizationAtLevel(level, ownership?.customization ?? {});
  const prestige = isPrestige(character.rarity) && !missing;
  const ref = useTilt<HTMLDivElement>(tilt && !missing, size === 'lg' ? 1.2 : 1, prestige);
  useCardPresence(ref, prestige || (!missing && level >= 7));
  const fxId = `fx${useId().replace(/:/g, '')}`;
  const customImage = missing || character.id < 0 ? null : character.customImageUrl ?? ownership?.customImageUrl ?? null;
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const useCustom = customImage && customImage !== failedImage;
  const large = useCustom ? customImage : character.imageUrl;
  const medium = useCustom ? customImage : character.imageMediumUrl;
  const image = large ?? medium;
  const srcSet = large && medium && large !== medium ? `${medium} 100w, ${large} 230w` : undefined;
  const sizes = size === 'lg' ? '340px' : size === 'sm' ? '120px' : '(min-width: 760px) 220px, 46vw';
  const starred = Boolean(ownership?.isFavorite);
  const cardNametag = nametag === undefined ? ownership?.nametag : nametag;
  const { data: ownedCharacters } = useOwnedCharacters();
  const owned = missing ? false : ownedCharacters?.has(character.id);
  const style = {
    '--card-tint': character.anime?.coverColor ?? '#171E42',
    '--card-name-color': customization.nameColor ? CARD_COLOR_HEX[customization.nameColor] : 'var(--ice)',
    '--card-aura-color': customization.haloColor ? CARD_COLOR_HEX[customization.haloColor] : 'var(--rarity)',
  } as CSSProperties;
  const combat = character.combat && !missing ? scaleCombatStats(character.combat, level) : null;
  const upgradeClasses = Array.from({ length: Math.max(0, level - 1) }, (_, index) => ` card--up${index + 2}`).join('');

  const rarityText = character.id < 0 ? '???' : rarityLabel(character.rarity);
  const label = [
    character.name,
    customization.nickname,
    cardNametag,
    rarityText,
    owned === undefined ? null : owned ? t('card.owned') : t('card.notOwned'),
    ownership ? t('card.level', { level }) : null,
    ownership ? t('card.fragments', { count: ownership.fragments }) : null,
    combat ? t('card.combat', { attack: combat.attack, defense: combat.defense }) : null,
    customization.signature,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      ref={ref}
      className={`card card--${size} rarity-${character.rarity}${missing ? ' card--missing' : upgradeClasses}${customization.nickname ? ' card--named' : ''}${customization.signature ? ' card--signed' : ''}${pressed ? ' card--picked' : ''}`}
      style={style}
      data-portrait={customization.portrait ?? undefined}
    >
      {prestige && <PrestigeAura rarity={character.rarity} idPrefix={fxId} lite={size === 'sm'} rich={size === 'lg'} />}
      {!missing && level >= 6 && <span className="card__level-halo" aria-hidden="true" />}
      <div className="card__body">
        <div className="card__art">
          {image ? (
            <img
              className="card__image"
              src={image}
              srcSet={srcSet}
              sizes={srcSet ? sizes : undefined}
              alt=""
              loading={eager ? 'eager' : 'lazy'}
              decoding="async"
              width={230}
              height={345}
              onLoad={(event) => event.currentTarget.classList.add('is-loaded')}
              onError={() => { if (customImage) setFailedImage(customImage); }}
            />
          ) : (
            <span className="card__image card__image--empty" />
          )}
          {!missing && <span className="card__holo" aria-hidden="true" />}
          {!missing && <span className="card__glare" aria-hidden="true" />}
          {prestige && <PrestigeFx rarity={character.rarity} idPrefix={fxId} lite={size === 'sm'} rich={size === 'lg'} />}
          {!missing && level >= 2 && (
            <span
              className="card__evolution"
              data-frame={customization.frame ?? 'precision'}
              data-finish={customization.finish ?? 'silk'}
              data-aura={customization.aura ?? 'orbit'}
              aria-hidden="true"
            >
              <span className="card__evolution-foil" />
              {level >= 3 && <span className="card__evolution-facets" />}
              {level >= 4 && <span className="card__evolution-frame" />}
              {level >= 8 && <span className="card__evolution-inscription" />}
              {level >= 9 && (
                <span className="card__evolution-orbit">
                  {Array.from({ length: size === 'sm' ? 3 : 6 }, (_, index) => <i key={index} />)}
                </span>
              )}
              {level >= 10 && <span className="card__evolution-crest"><Sparkles size={size === 'sm' ? 12 : 22} /></span>}
            </span>
          )}
          {ownership?.lock && !missing && (
            <span className="card-lock-badge" title={t('card.locked')}>
              <LockIcon size={12} />
            </span>
          )}
          {ownership && !missing && (
            <FragmentCrystal
              className="card__crystal"
              count={ownership.fragments}
              rarity={character.rarity}
              imageUrl={character.imageMediumUrl}
              size={size === 'lg' ? 'lg' : size === 'sm' ? 'sm' : 'md'}
              sparkle={sparkleCrystal}
            />
          )}
          {combat && (
            <span className="card__stats" aria-hidden="true">
              <span className="card__stat card__stat--attack" title={t('card.attack')}>
                <Sword size={size === 'lg' ? 14 : 11} strokeWidth={2.5} />
                {combat.attack}
              </span>
              <span className="card__stat card__stat--defense" title={t('card.defense')}>
                <Shield size={size === 'lg' ? 14 : 11} strokeWidth={2.5} />
                {combat.defense}
              </span>
            </span>
          )}
        </div>
        <span className="card__rarity">
          {rarityText}
        </span>
        {ownership && !missing && (
          <span className="card__level-mark" title={t('detail.levelOf', { level, max: MAX_CARD_LEVEL })} aria-hidden="true">
            <strong>{level}</strong><small>/{MAX_CARD_LEVEL}</small>
          </span>
        )}
        {!missing && level >= 5 && (
          <span className="card__emblem" aria-hidden="true">
            <CardEmblem emblem={customization.emblem ?? 'star'} size={size === 'lg' ? 22 : size === 'sm' ? 12 : 16} />
          </span>
        )}
        {tag && <span className="card__tag">{tag}</span>}
        {onFavorite ? (
          <button
            type="button"
            className="card__fav"
            aria-pressed={starred}
            aria-label={starred ? t('card.favoriteRemove', { name: character.name }) : t('card.favoriteAdd', { name: character.name })}
            onClick={(event) => {
              event.stopPropagation();
              onFavorite();
            }}
          >
            <StarIcon size={16} filled={starred} />
          </button>
        ) : starred && <span className="card__favorite" aria-hidden="true" />}
        {onWish ? (
          <button
            type="button"
            className={`card__wish${onFavorite || tag ? ' card__wish--secondary' : ''}`}
            aria-pressed={wished}
            aria-label={wished ? t('card.wishRemove', { name: character.name }) : t('card.wishAdd', { name: character.name })}
            onClick={(event) => {
              event.stopPropagation();
              onWish();
            }}
          >
            <HeartIcon size={16} filled={wished} />
          </button>
        ) : (
          wished && <span className="card__wish-marker" aria-hidden="true"><HeartIcon size={13} filled /></span>
        )}
        <span className="card__plate">
          {cardNametag && <span className="card__nametag">{cardNametag}</span>}
          <span className="card__name">{customization.nickname ?? character.name}</span>
          {customization.nickname && <span className="card__original-name">{character.name}</span>}
          {character.anime && <span className="card__anime">{character.anime.title}</span>}
          {customization.signature && <span className="card__signature">{customization.signature}</span>}
          {owned !== undefined && (
            <span className={`card__ownership card__ownership--${owned ? 'owned' : 'missing'}`}>
              {owned ? <CheckIcon size={14} /> : <CloseIcon size={14} />}
            </span>
          )}
        </span>
      </div>
      {onSelect && (
        <button type="button" className="card__hit" onClick={onSelect} aria-pressed={pressed}>
          <span className="visually-hidden">{label}</span>
        </button>
      )}
      {!onSelect && <span className="visually-hidden">{label}</span>}
      {action && <span className="card__action">{action}</span>}
    </div>
  );
}
